// eslint-disable-next-line import/no-extraneous-dependencies
import API, { graphqlOperation } from '@aws-amplify/api';

const MAPPING = {
  accessTo: ['locationConfigs', 'accessTo'],
  address: ['operationConfigs', 'address'],
  adminEmail: ['locationConfigs', 'adminEmail'],
  aprivaDeviceAddress: ['locationConfigs', 'aprivaDeviceAddress'],
  baseCommission: ['locationConfigs', 'baseCommission'],
  directions: ['operationConfigs', 'directions'],
  displayVendImages: ['locationConfigs', 'holiday', 'image'],
  features: ['locationConfigs', 'features'],
  fridgeModel: ['locationConfigs', 'fridgeModel'],
  fridgeSchema: ['locationConfigs', 'fridgeSchema'],
  groups: ['locationConfigs', 'groups'],
  holidayScreenEnabled: ['locationConfigs', 'holiday', 'enabled'],
  holidayScreenEndDate: ['locationConfigs', 'holiday', 'endDate'],
  holidayScreenFridgeSpecific: ['locationConfigs', 'holiday', 'specific'],
  holidayScreenStartDate: ['locationConfigs', 'holiday', 'startDate'],
  hostname: ['locationConfigs', 'hostname'],
  hours: ['locationConfigs', 'hours'],
  id: ['id'],
  ip: ['locationConfigs', 'ip'],
  lastModified: ['lastModified'],
  latitude: ['locationConfigs', 'coordinates', 'latitude'],
  layoutName: ['locationConfigs', 'items', 'layout'],
  loggingLevel: ['locationConfigs', 'loggingLevel'],
  longitude: ['locationConfigs', 'coordinates', 'longitude'],
  onlineDate: ['locationConfigs', 'activation', 'online'],
  outOfOrder: ['locationConfigs', 'outOfOrder'],
  parAlgo: ['locationConfigs', 'par', 'algo'],
  parMapMode: ['locationConfigs', 'par', 'mode'],
  paymentGateway: ['locationConfigs', 'paymentGateway'],
  percentage: ['locationConfigs', 'percentage'],
  pin: ['locationConfigs', 'pin'],
  prettyName: ['locationConfigs', 'prettyName'],
  priceMapTemplate: ['locationConfigs', 'items', 'priceTier'],
  prod2: ['status', 'tech'],
  prod: ['operationConfigs', 'prod'],
  publicStatus: ['status', 'public'],
  purchaseStatus: ['status', 'purchase'],
  reserve: ['locationConfigs', 'reserve'],
  route: ['operationConfigs', 'route'],
  serialNumber: ['locationConfigs', 'serialNumber'],
  serialPort: ['locationConfigs', 'serialPort'],
  site: ['operationConfigs', 'site'],
  status: ['status', 'ops'],
  stockingSchedule: ['operationConfigs', 'stockingSchedule'],
  stockingStatus: ['operationConfigs', 'stockingStatus'],
  stop: ['operationConfigs', 'stop'],
  taxRate: ['locationConfigs', 'taxRate'],
  threshold: ['locationConfigs', 'threshold'],
  timeZone: ['locationConfigs', 'timeZone'],
  touchSequence: ['locationConfigs', 'touchSequence'],
  type: ['type'],
  updateReason: ['updateReason'],
  useMock: ['locationConfigs', 'useMock'],
  vertical: ['locationConfigs', 'vertical'],
  workHours: ['locationConfigs', 'workHours'],
};
const JSON_FIELDS = ['stockingSchedule', 'workHours', 'updateReason'];

const LOCATION_FIELDS = `
  id
  lastModified
  updateReason
  status { ops public purchase tech }
  timeZone
  type
  operationConfigs { address directions prod route site stockingSchedule stockingStatus stop }
  locationConfigs {
    accessTo
    activation { install online  stocking }
    adminEmail
    aprivaDeviceAddress
    baseCommission
    coordinates { latitude longitude }
    fridgeSchema
    fridgeModel
    features { name type value }
    fridgeSchema
    holiday { enabled specific startDate endDate }
    hostname
    hours
    inventory { itemsBySlot { expirationDate productionDate slot { x y z } uniqueId vendItemName } messageId }
    ip
    items { layout priceTier }
    loggingLevel
    par { algo mode }
    paymentGateway
    percentage
    pin
    prettyName
    reserve
    serialNumber
    serialPort
    taxRate
    threshold
    timeZone
    touchSequence
    useMock
    vertical
    workHours
}`;
function graphqlToDynamo(GraphQLLocation) {
  const newObject = {};
  // TODO: rewrite map to prevent ESlint issues
  Object.keys(MAPPING).map((oldkey) =>
    MAPPING[oldkey].map(
      // eslint-disable-next-line no-return-assign
      (newkey) =>
        (newObject[oldkey] =
          // eslint-disable-next-line no-nested-ternary
          oldkey in newObject && newObject[oldkey] !== undefined
            ? newObject[oldkey][newkey]
            : newObject[oldkey] === undefined
            ? GraphQLLocation[newkey]
            : undefined),
    ),
  );
  JSON_FIELDS.forEach((json_field) => {
    if (json_field in newObject && newObject[json_field] !== undefined) {
      newObject[json_field] = JSON.parse(newObject[json_field]);
    }
  });
  return newObject;
}
// while (order.data.ordersByDay.nextToken !== null) {
//           console.log(order.data.ordersByDay.nextToken);
//           order = await Order(
//             order.data.ordersByDay.nextToken,
//             this.state.searchDates[search_date],
//             null,
//           );
export async function LocationIDs() {
  const response = await API.graphql(
    graphqlOperation(`
      query GetLocations{
        locations{
          locations {
            status { public }
            locationConfigs {
              coordinates { latitude longitude }
            }
            id
            type
          }
        }
      }
    `),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function LocationStatuses() {
  const response = await API.graphql(
    graphqlOperation(`
      query GetLocationStatuses{
        locations{
          locations {
            id
            status { ops }
          }
        }
      }
    `),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export const OPS_LOCATIONS_MAX_PAGES = 20;

export class OpsLocationsTruncationError extends Error {
  constructor(fnName, maxPages) {
    super(
      `${fnName} exceeded OPS_LOCATIONS_MAX_PAGES (${maxPages}); backend returned more pages than the client will fetch.`,
    );
    this.name = 'OpsLocationsTruncationError';
  }
}

async function paginateOpsLocations({ query, filter, fnName }) {
  let allLocations = [];
  let currentToken = null;
  let pages = 0;

  do {
    const response = await API.graphql(
      graphqlOperation(query, {
        filter: { ...filter, nextToken: currentToken },
      }),
    );
    if (response.error) {
      throw response.error[0];
    }
    allLocations = allLocations.concat(response.data.opsLocations.locations);
    currentToken = response.data.opsLocations.nextToken;
    pages += 1;
  } while (currentToken && pages < OPS_LOCATIONS_MAX_PAGES);

  if (currentToken) {
    throw new OpsLocationsTruncationError(fnName, OPS_LOCATIONS_MAX_PAGES);
  }

  return {
    data: { opsLocations: { locations: allLocations, nextToken: null } },
  };
}

export async function OpsLocations(filter = {}) {
  return paginateOpsLocations({
    query: `
      query GetOpsLocations($filter:filterOpsLocation){
        opsLocations(filter: $filter){
          locations {
            id
            type
            fulfillmentCenter
            opportunityName
          }
          nextToken
        }
      }
    `,
    filter,
    fnName: 'OpsLocations',
  });
}
export async function UploadPars(UploadParsInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation uploadParMaps($input: [UploadParsInput!]!) {
        uploadParMaps(input: $input)
      }`,
      {
        input: UploadParsInput,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.uploadParMaps;
}
export async function ViewPars(dropOffDate, location) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query ViewPars($dropOffDate: String!, $location: String!){
        jitLocations(input: {dropOffDate: $dropOffDate, location: $location}) {
          pars {
            itemName
            count
          }
        }
      }`,
      {
        dropOffDate,
        location,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function OrderMeta(
  nextToken,
  productionDate = null,
  deliveryDate = null,
) {
  const response = await API.graphql(
    graphqlOperation(
      `
    query ListOrders($nextToken: String, $productionDate: AWSDate, $deliveryDate: AWSDate){
      ordersByDay(productionDate:$productionDate,nextToken:$nextToken,deliveryDate:$deliveryDate,count:1000){
        nextToken
        orders {
          clientCode
          orderOwner
          orderType
          locationId
          uniqueId
          addressForDelivery
          deliveryDate
          dropOffDate
          deliverySpecialNotes
          orderUploadId
          stockingIndex
        }
      }
    }
  `,
      {
        nextToken,
        productionDate: productionDate !== undefined ? productionDate : null,
        deliveryDate: deliveryDate !== undefined ? deliveryDate : null,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function DeleteScheduleChange(DeleteParameters) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation deleteScheduleChange($input: DeleteScheduleChangeInput!) {
        deleteScheduleChange(input: $input) {
          uuid
          actionTime
        }
      }`,
      {
        input: DeleteParameters,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.deleteScheduleChange;
}
export async function CreateScheduleChange(newChanges) {
  // wrap `value` in an object so that it matches the type expected by the graphQL endpoint
  newChanges = newChanges.map((newChange) => ({
    ...newChange,
    value: JSON.stringify(newChange.value),
  }));
  const response = await API.graphql(
    graphqlOperation(
      `mutation createScheduleChange($input: [ScheduleChangeInput!]!) {
        createScheduleChange(input: $input) {
          id
          user
          uuid
          actionTime
          version
          change {
            id
            table
            attribute
            value
          }
        }
      }
    `,
      {
        input: newChanges,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  const formattedResponse = response.data.createScheduleChange.map(
    (changeResponse) => {
      changeResponse.change.value = JSON.parse(changeResponse.change.value);
      return changeResponse;
    },
  );
  return formattedResponse;
}

export async function ReadScheduleChange(loc_id) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query readScheduleChange($filter: filterScheduleChange!) {
        readScheduleChange(filter: $filter) {
          actionTime
          change {
            id
            table
            attribute
            value
          }
          id
          user
          uuid
          version
        }
      }
    `,
      {
        filter: { id: loc_id },
      },
    ),
  );
  console.log(response);
  if (response.error) {
    throw response.error[0];
  }

  const formattedResponse = response.data.readScheduleChange.map(
    (changeResponse) => {
      changeResponse.change.value = JSON.parse(changeResponse.change.value);
      return changeResponse;
    },
  );

  return formattedResponse;
}

export async function UpdateLocation(locationInput) {
  if ('covid19' in locationInput) {
    locationInput.featureFlags = {
      covid19: locationInput.covid19,
    };
    delete locationInput.covid19;
  }
  // stringify updateReason so that it matches the type expected by the graphQL endpoint
  locationInput.updateReason = JSON.stringify(locationInput.updateReason);
  const response = await API.graphql(
    graphqlOperation(
      `mutation UpdateLocation($input: UpdateLocationInput!) {
        updateLocation(input: $input) {
          ${LOCATION_FIELDS}
      }
     }
    `,
      { input: locationInput },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return graphqlToDynamo(response.data.updateLocation);
}

export async function Location(id) {
  const response = await API.graphql(
    graphqlOperation(
      `query GetLocations($filter: filterLocation!) {
      locations(filter: $filter) {
        locations {
        ${LOCATION_FIELDS}
       }
      }
     }
    `,
      {
        filter: {
          id,
        },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.locations.locations
    .filter((location) => location.type === 'FRIDGE')
    .map((location) => graphqlToDynamo(location));
}

export async function GetQueueMessages(
  locationId,
  type,
  nextToken = null,
  count = 1000,
  version = '',
) {
  const id = type ? `${locationId}#${type}` : locationId;
  const response = await API.graphql(
    graphqlOperation(
      `query GetQueueMessages($filter: filterQueueMessages!) {
        readQueueMessages(filter: $filter) {
          queueMessages {
            timestamp
          }
          nextToken
        }
      }`,
      {
        filter: {
          id,
          count,
          version,
          nextToken,
        },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function CreateLocation(locationInput) {
  console.log(locationInput);
  // TODO: rewrite loop to prevent restricted syntax
  // eslint-disable-next-line no-restricted-syntax
  for (const [key, value] of Object.entries(locationInput)) {
    if (value === '') {
      delete locationInput[key];
    }
  }

  const response = await API.graphql(
    graphqlOperation(
      `mutation CreateLocation($input: CreateLocationInput!) {
        createLocation(input: $input) {
          id
        }
    }
    `,
      { input: locationInput },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return graphqlToDynamo(response.data.createLocation);
}

async function PaginatedLocations(filter = {}, paginationToken, getAllData) {
  let inputFilter = {};
  // This function will default to query for 2500 locations with status LIVE unless a filter is provided
  if (getAllData) {
    inputFilter = {
      filter: {
        count: 2500,
      },
    };
  } else if (Object.keys(filter).length === 0) {
    inputFilter = {
      filter: {
        count: 2500,
        status: 'LIVE',
      },
    };
  } else {
    inputFilter.filter = filter;
  }
  if (paginationToken) {
    inputFilter.filter.nextToken = paginationToken;
  }
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetLocations($filter:filterLocation!){
        locations(filter:$filter){
          nextToken
          locations {
            operationConfigs { address directions prod route site stockingSchedule stockingStatus stop }
            status { ops public tech }
            locationConfigs { prettyName }
            id
            type
            lastModified
          }
        }
      }
    `,
      inputFilter,
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}
export async function Locations(filter = {}, getAllData = false) {
  let locations = [];
  let response = await PaginatedLocations(filter, null, getAllData);

  locations = locations.concat(response.data.locations.locations);
  while (response.data.locations.nextToken) {
    response = await PaginatedLocations(
      filter,
      response.data.locations.nextToken,
      getAllData,
    );
    locations = locations.concat(response.data.locations.locations);
  }
  if (!getAllData) {
    return locations
      .filter((location) => location.type === 'FRIDGE')
      .map((location) => graphqlToDynamo(location));
  }
  return locations;
}

export async function FridgeHealth(inputFilter) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query fridgeHealth($filter:FilterFridgeHealth!) {
        fridgeHealth(filter:$filter){
          fridgeData {
            activeTicket
            locationId
            temperature
            hdmi
            elo
            cardReader
            electricalLayout
            jofemarState
            outOfOrder
            outOfOrderTs
            safetyHold
            safetyHoldTs
            healthStatusTimestamp
            timestamp
            temperatureSetpoint
            networkSpeed {
              previousSpeed
            }
            timezone
            inaccessible
            serverVersion
            allReasons
            activeWorkOrders {
              wo
              assignto
              brief_desc
              date_wo
              sch_date
              posname
              time
              time_zone
            }
            connectedLayout{
              rowValue{
                x,
                y
              }
            }
            recycleDoorState {
            doorState
            lockState
          }
            sundryDoorState {
              doorState
              lockState
            }
            liteFridgeState {
              topShellLatchState
              bottomShellLatchState
              shellLockState
              status
            }
          }
        }
      }
    `,
      {
        filter: inputFilter,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function FridgeHealthStockingDash(inputFilter) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query fridgeHealth($filter:FilterFridgeHealth!) {
        fridgeHealth(filter:$filter){
          fridgeData {
            locationId
            jofemarState
            safetyHold
            safetyHoldTs
          }
        }
      }
    `,
      {
        filter: inputFilter,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetFridgeEvents(filter) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetFridgeEvents($filter:FilterFridgeEvents!) {
        getFridgeEvents(filter:$filter) {
            location
            eventType
            timestamp
        }
      }
    `,
      {
        filter,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function CreateQueueMessage(inputInfo) {
  const response = await API.graphql(
    graphqlOperation(
      `
      mutation SendQueueMessage($input: InputQueueMessage!){
        createQueueMessage(input: $input){
          location,
          message {
            messageId,
            messageType,
            payload{
              operation,
              resource
            }
          },
          timestamp
        }
      }
    `,
      {
        input: inputInfo,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function CompleteComputerSwapRequest(locationId) {
  const response = await API.graphql(
    graphqlOperation(
      `
      mutation SendComputerSwapComplete($locationId: String!){
        sendConfirmedInventoryAndConfirmedReservation(locationId: $locationId)
      }
    `,
      {
        locationId,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function UpdateOpsLocation(opsLocationInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation UpdateOpsLocation($input: OpsLocationInput!) {
        updateOpsLocation(input: $input) {
          id
          route
          stop
      }
     }
    `,
      { input: opsLocationInput },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.updateOpsLocation;
}

export async function CreateLocationRoutes(locationRoutesInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation CreateLocationRoutes($input: [CreateLocationRouteInput!]!) {
        createLocationRoutes(input: $input) {
          stockingDate
          market
          id
          route
          stop
          stockingIndex
          stopType
          calculatedStartTime
          calculatedEndTime
      }
     }
    `,
      { input: locationRoutesInput },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.updateLocationRoute;
}

export async function GetLocationRoutes(filterLocationRoutes) {
  const response = await API.graphql(
    graphqlOperation(
      `query LocationRoutes($filter: filterLocationRoutes!) {
        locationRoutes(filter: $filter) {
          routes {
            id
            stockingDate
            market
            route
            stop
            stockingIndex
            stopType
            fulfillmentCenter
            toteCountPerStop
            toteCountPerRoute
            onlineDate
            stockedTimeUTC
          }
      }
     }
    `,
      { filter: filterLocationRoutes },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.locationRoutes;
}

export async function FridgeSoftware() {
  const response = await API.graphql(
    graphqlOperation(`
      query FridgeSoftware{
        getFridgeArtifacts{
          artifactType
          versions {
            versionName
            fridgesDeployedTo
          }
        }
      }
    `),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetAvailableSoftwareVersions() {
  const response = await API.graphql(
    graphqlOperation(`
    query QueryAvailableArtifacts{
      getAvailableFridgeArtifacts{
        artifactType
        availableVersions
        }
      }
    `),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function FridgeSoftwarePilots(onlyShowActive) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query QueryFridgeSoftwarePilots($onlyShowActive: Boolean) {
  getFridgeSoftwarePilots(onlyShowActive: $onlyShowActive) {
    fridgeIds
    displayName
    startDate
    endDate
    stage
    description
    active
  }
  }
  `,
      { onlyShowActive },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function PutFridgeSoftwarePilot(pilotEntry) {
  const response = await API.graphql(
    graphqlOperation(
      `
      mutation PutFridgeSoftwarePilot($input: InputFridgeSoftwarePilot!) {
        putFridgeSoftwarePilot(input: $input)
      }
  `,
      { input: pilotEntry },
    ),
  );
  return response;
}

export async function UpdateFridgeArtifacts(artifactType, version, fridges) {
  const response = await API.graphql(
    graphqlOperation(
      `
      mutation UpdateFridgeArtifacts($artifactType: ArtifactType!, $version: String!, $fridges: [String]!) {
        updateFridgeArtifacts(artifactType: $artifactType, version: $version, fridges: $fridges) {
            sk
            pk
            artifactType
            objectKey
            }
}
  `,
      {
        artifactType,
        version,
        fridges,
      },
    ),
  );
  return response;
}

export async function GetDriverAction(FilterDriverActions) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetDriverAction($filterDriverActions: FilterDriverActions!){
        getDriverAction(filter: $filterDriverActions) {
        actions {
          data
          locationId
          _lastModified
         }
         nextToken
        }
      }`,
      {
        filterDriverActions: FilterDriverActions,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function CreateDriverActionMutation(input) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation CreateDriverAction($input:CreateDriverActionInput!) {
        createDriverAction(input:$input) {
          eventType
          locationId
          data
        }
      }`,
      {
        input,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

/**
 * GetAvailableFridgeSchemas - Lightweight query for fetching schema IDs and pricing types
 *
 * Returns only schemaId and pricingType fields for dropdown/selection purposes.
 * Used in: ItemPricesLanding, LocationFormGraphQL, EditLocationGraphQL, MassSchedulingChangeGraphQL
 *
 * Does not support input filtering - returns all schemas.
 * For full schema condition details with filtering, use GetFridgeSchemasTemplates instead.
 */
export async function GetAvailableFridgeSchemas() {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetFridgeSchemasTemplates($input: FridgeSchemaTemplatesInput) {
        getFridgeSchemasTemplates(input: $input) {
          pricingType
          schemaId
        }
      }
    `,
      { input: {} },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }

  return response.data.getFridgeSchemasTemplates;
}

/**
 * GetFridgeSchemasTemplates - Full query for fetching complete schema condition details
 *
 * Returns all schema condition fields including schemaType, timeBoundSchedule, discountAmount, etc.
 * Used in: SchemaConditionsLanding
 *
 * Supports input filtering by schemaIds:
 * - Pass empty object {} to get all schemas
 * - Pass { schemaIds: ["SCHEMA1", "SCHEMA2"] } to filter specific schemas
 */
export async function GetFridgeSchemasTemplates(input = {}) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetFridgeSchemasTemplates($input: FridgeSchemaTemplatesInput) {
        getFridgeSchemasTemplates(input: $input) {
          schemaId
          pricingType
          schemaType
          timeBoundSchedule
          discountAmount
          discountUI
          priceDisplayType
          expiringDays
          expiringLayers {
            hours
            discount
          }
          platform
          sharedSubsidyClientDiscount
        }
      }
    `,
      { input },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }

  return response.data.getFridgeSchemasTemplates;
}

export async function UpdateSchemaTemplatesBatch(items) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation updateSchemaTemplatesBatch($input: UpdateSchemaTemplatesBatchInput!) {
        updateSchemaTemplatesBatch(input: $input) {
          success
          message
          results {
            index
            schemaId
            success
            message
            errors
          }
        }
      }`,
      {
        input: { items },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response?.data?.updateSchemaTemplatesBatch;
}

export async function CreateSchemaTemplatesBatch(items) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation createSchemaTemplatesBatch($input: CreateSchemaTemplatesBatchInput!) {
        createSchemaTemplatesBatch(input: $input) {
          success
          message
          results {
            index
            schemaId
            success
            message
            errors
          }
        }
      }`,
      {
        input: { items },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response?.data?.createSchemaTemplatesBatch;
}

export async function GenerateAprivaDeviceAddress(locationId) {
  const response = await API.graphql(
    graphqlOperation(
      `
      mutation GenerateAprivaDeviceAddress($input: GenerateAprivaDeviceAddressInput!) {
        generateAprivaDeviceAddress(input: $input)
      }
    `,
      {
        input: {
          locationId,
        },
      },
    ),
  );
  if (response.errors) {
    throw response.errors[0];
  }
  return response;
}

export async function GetPriceMapTemplates() {
  const response = await API.graphql(
    graphqlOperation(`
      query GetPriceMapTemplates {
        getPriceMapTemplates
      }

    `),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.getPriceMapTemplates;
}

export async function GetPlanogramNames() {
  const response = await API.graphql(
    graphqlOperation(`
      query getPlanogramNames {
        getPlanogramNames
      }
    `),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.getPlanogramNames;
}

// Fetch a layout by name or location to read attributes such as physicalFridgeConfig
export async function GetLayout(input) {
  const response = await API.graphql(
    graphqlOperation(
      `
        query getLayout($input: filterLayout!) {
          getLayout(input: $input) {
            items {
              vendItemName
              maxPar
              physicalFridgeConfig
              slot { x y }
            }
          }
        }
      `,
      { input },
    ),
  );
  if (response.errors) {
    throw response.error[0];
  }
  return response.data.getLayout;
}

// Fetch the menu for a location. Used by the Seed Fridge Inventory page to
// resolve each vendItem's shelfLife for computing expiration dates client-side,
// and by the View Inventory page to map vendItemName -> categories for the
// "sellable inventory" totals.
export async function GetMenuShelfLife(locationId) {
  const response = await API.graphql(
    graphqlOperation(
      `query GetMenuShelfLife($filter: filterMenu!) {
        menu(filter: $filter) {
          items {
            id
            shelfLife
            maxPerSlot
            categories
          }
        }
      }`,
      { filter: { locationId } },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.menu.items;
}

export async function GetOpsLocations(nextToken, status = null) {
  const response = await API.graphql(
    graphqlOperation(
      `
    query GetOpsLocations($filter: filterOpsLocation){
      opsLocations(filter:$filter){
        locations {
          id
          type
          routingTag
          parkToFridgeInstructions
          fulfillmentCenter
        }
        nextToken
      }
    }`,
      {
        filter: {
          nextToken,
          status,
        },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetOpsLocationsStockingDash(nextToken, status = null) {
  const response = await API.graphql(
    graphqlOperation(
      `
    query GetOpsLocations($filter: filterOpsLocation){
      opsLocations(filter:$filter){
        locations {
          id
          type
          accountTier
          onlineDate
          fulfillmentCenter
        }
        nextToken
      }
    }`,
      {
        filter: {
          nextToken,
          status,
        },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetOpsLocationsTypeOnly(nextToken, status = null) {
  const response = await API.graphql(
    graphqlOperation(
      `
    query GetOpsLocations($filter: filterOpsLocation){
      opsLocations(filter:$filter){
        locations {
          id
          type
        }
        nextToken
      }
    }`,
      {
        filter: {
          nextToken,
          status,
        },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetOpsLocationsRetailers(filter = {}) {
  return paginateOpsLocations({
    query: `
      query GetOpsLocations($filter:filterOpsLocation){
        opsLocations(filter: $filter){
          nextToken
          locations {
            opportunityName
            id
            type
            routingTag
            parkToFridgeInstructions
            fulfillmentCenter
            deliveryInstructions
            timeZone
            containerType
            status
            region
            operatingModel
            retailer
            sundriesMultiplier
            linehaulFirstMile
            linehaulMiddleMile
          }
        }
      }
    `,
    filter,
    fnName: 'GetOpsLocationsRetailers',
  });
}

/* Get fulfillment centers with minimal fields for Nextmv - uses pagination */
export async function GetOpsLocationsFulfillmentCenters(nextToken = null) {
  const response = await API.graphql(
    graphqlOperation(
      `
    query GetOpsLocations($filter: filterOpsLocation){
      opsLocations(filter:$filter){
        locations {
          fulfillmentCenter
          timeZone
        }
        nextToken
      }
    }`,
      {
        filter: {
          nextToken,
          status: ['LIVE'],
        },
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

/* Adding changes for the Create Nextmv */
export async function CreateNextmv(CreateNextmvRunInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation CreateNextmvRun($input: CreateNextmvRunInput!) {
      createNextmvRun(input: $input) {
      statusCode
      nextmvRunId
      message
      }
    }`,
      {
        input: CreateNextmvRunInput,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

/* Get Traffic Configuration for a fulfillment center */
export async function GetTrafficConfiguration(fulfillmentCenter) {
  const response = await API.graphql(
    graphqlOperation(
      `query GetTrafficConfigurationForFulfillmentCenter($fulfillmentCenter: String!) {
        getTrafficConfigurationForFulfillmentCenter(input: {fulfillmentCenter: $fulfillmentCenter}) {
          trafficConfiguration
        }
      }`,
      { fulfillmentCenter },
    ),
  );
  if (response.errors?.length) {
    throw response.errors[0];
  }
  return response.data.getTrafficConfigurationForFulfillmentCenter;
}

/* Update Traffic Configuration */
export async function UpdateTrafficConfiguration(trafficConfiguration) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation UpdateTrafficConfiguration($input: [UpdateTrafficConfigurationInput!]!) {
        updateTrafficConfiguration(input: $input) {
          __typename
        }
      }`,
      { input: trafficConfiguration },
    ),
  );
  if (response.errors) {
    throw response.errors[0];
  }
  return response.data.updateTrafficConfiguration;
}

export async function GetJitManagement(filterLocationRoutes, JitLocationInput) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetJitManagement($filterLR: filterLocationRoutes!, $inputJM: JitLocationInput!){
        locationRoutes(filter: $filterLR) {
          routes {
            id
            stockingDate
            market
            route
            stop
            stockingIndex
            stopType
            stockedTimeUTC
          }
        }
        jitLocations(input: $inputJM) {
          dropOffDate
          jit
          location
          reason
          email
        }
      }`,
      {
        filterLR: filterLocationRoutes,
        inputJM: JitLocationInput,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function UpdateJITLocationStatus(JITLocationStatusInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation UpdateJITLocationStatus($input: [JITLocationStatusInput!]!) {
        updateJITLocationStatus(input: $input) {
            message
            orderId
            jitStatusUpdate
            jitAllocationItemsClear
          stockingId
        }
      }`,
      {
        input: JITLocationStatusInput,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetPackingSlips(GetPackingSlipsV3Filter) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetPackingSlips($filter: GetPackingSlipsV3Filter!){
        getPackingSlipsV3(filter: $filter){
          locationId
          route
          dropOffDate
          uniqueId
          createDate
          stockingIndex
          type
          clientCode
          status
        }
      }`,
      {
        filter: GetPackingSlipsV3Filter,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function GetPackingSlipsV3Paginated(filter, nextToken = null) {
  const filterWithToken = {
    ...filter,
    nextToken: nextToken || null,
  };

  const response = await API.graphql(
    graphqlOperation(
      `
      query GetPackingSlipsV3Paginated($filter: GetPackingSlipsV3Filter!){
        getPackingSlipsV3(filter: $filter){
          packingSlips {
            locationId
            route
            dropOffDate
            uniqueId
            createDate
            stockingIndex
            type
            clientCode
            status
          }
          nextToken
        }
      }`,
      {
        filter: filterWithToken,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function PaginatedPackingSlip(dropOffDate, nextToken) {
  const response = await API.graphql(
    graphqlOperation(
      `
  query PaginatedPackingSlip($dropOffDate: String!, $nextToken: String){
    paginatedPackingSlips(dropOffDate:$dropOffDate,nextToken:$nextToken){
      nextToken
      packingSlips {
        qrCode
        date
        driver
        dropOffDate
        location
        uniqueId
        type
        clientCode
      }
    }
  }`,
      {
        nextToken,
        dropOffDate,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function SchedulePlanogramUpdate(schedulePlanogramUpdateInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation schedulePlanogramUpdate($input: [PlanogramUpdate!]!) {
        schedulePlanogramUpdate(input: $input) {
          successCount
          errors
          totalCount
        }
      }`,
      {
        input: schedulePlanogramUpdateInput,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.schedulePlanogramUpdate;
}

export async function GetFridgeSegmentEvents(filter) {
  const response = await API.graphql(
    graphqlOperation(
      `
      query GetFridgeSegmentEvents($filter:FilterFridgeSegmentEvents!) {
        getFridgeSegmentEvents(filter:$filter) {
            eventType
            date
            payload
        }
      }
    `,
      {
        filter,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response;
}

export async function UpdateItemPrices(itemPricesInput) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation updateItemPrices($input: [ItemPriceUpdateInput!]!) {
        updateItemPrices(input: $input) {
          success
          updatedSchemas
          message
          rowErrors {
            schemaId
            item
            price
            errors
          }
        }
      }`,
      {
        input: itemPricesInput,
      },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.updateItemPrices;
}

export async function GetItemPrices(input = {}) {
  const response = await API.graphql(
    graphqlOperation(
      `query getItemPrices($input: ItemPricesQueryInput!) {
        getItemPrices(input: $input) {
          items {
            schemaId
            item
            price
          }
          nextToken
        }
      }`,
      { input },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data.getItemPrices;
}

export async function GetInventory(input) {
  const response = await API.graphql(
    graphqlOperation(
      `query getInventory($input: getInventoryInput!) {
        getInventory(input: $input) {
          timestamp
          itemsBySlot {
            expirationDate
            productionDate
            slot {
              x y z
            }
            status
            uniqueId
            vendItemName
          }
        }
      }`,
      { input },
    ),
  );
  if (response.error) {
    throw response.error[0];
  }
  return response.data;
}

export async function GetAllDriverFixActions(createdAt, eventType) {
  const allActions = [];
  let nextToken = null;
  // Drain pagination — list view needs the full day of events.
  do {
    const filter = { createdAt, eventType };
    if (nextToken) filter.nextToken = nextToken;
    // eslint-disable-next-line no-await-in-loop
    const response = await GetDriverAction(filter);
    const page = response && response.data && response.data.getDriverAction;
    if (page && Array.isArray(page.actions)) {
      allActions.push(...page.actions);
    }
    nextToken = page && page.nextToken ? page.nextToken : null;
  } while (nextToken);
  return allActions;
}

let sleepScreensListCache = null;
let sleepScreensListCachedAt = 0;
const SLEEP_SCREENS_LIST_TTL_MS = 30 * 1000;

const invalidateSleepScreensListCache = () => {
  sleepScreensListCache = null;
  sleepScreensListCachedAt = 0;
};

const sleepScreenCache = new Map();
const SLEEP_SCREEN_TTL_MS = 30 * 1000;

export async function CreateSleepScreen(input) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation createSleepScreen($input: CreateSleepScreenInput!) {
        createSleepScreen(input: $input) {
          sleepScreenId
          videoUrl
          displayName
          description
          createdBy
          updatedBy
          createdAt
          updatedAt
        }
      }`,
      { input },
    ),
  );
  if (response.errors) {
    throw response.errors[0];
  }
  invalidateSleepScreensListCache();
  return response.data.createSleepScreen;
}

export async function ListSleepScreens() {
  if (
    sleepScreensListCache &&
    Date.now() - sleepScreensListCachedAt < SLEEP_SCREENS_LIST_TTL_MS
  ) {
    return sleepScreensListCache;
  }
  const response = await API.graphql(
    graphqlOperation(
      `query listSleepScreens {
        listSleepScreens {
          sleepScreenId
          videoUrl
          displayName
          description
          createdBy
          updatedBy
          createdAt
          updatedAt
        }
      }`,
    ),
  );
  if (response.errors) {
    throw response.errors[0];
  }
  sleepScreensListCache = response.data.listSleepScreens;
  sleepScreensListCachedAt = Date.now();
  return sleepScreensListCache;
}

export async function GetSleepScreen(sleepScreenId) {
  const cached = sleepScreenCache.get(sleepScreenId);
  if (cached && Date.now() - cached.cachedAt < SLEEP_SCREEN_TTL_MS) {
    return cached.data;
  }
  const response = await API.graphql(
    graphqlOperation(
      `query getSleepScreen($sleepScreenId: String!) {
        getSleepScreen(sleepScreenId: $sleepScreenId) {
          sleepScreenId
          videoUrl
          displayName
          description
          createdBy
          updatedBy
          createdAt
          updatedAt
        }
      }`,
      { sleepScreenId },
    ),
  );
  if (response.errors) {
    throw response.errors[0];
  }
  if (response.data.getSleepScreen) {
    sleepScreenCache.set(sleepScreenId, {
      data: response.data.getSleepScreen,
      cachedAt: Date.now(),
    });
  }
  return response.data.getSleepScreen;
}

export async function UpdateSleepScreen(input) {
  const response = await API.graphql(
    graphqlOperation(
      `mutation updateSleepScreen($input: UpdateSleepScreenInput!) {
        updateSleepScreen(input: $input) {
          sleepScreenId
          videoUrl
          displayName
          description
          createdBy
          updatedBy
          createdAt
          updatedAt
        }
      }`,
      { input },
    ),
  );
  if (response.errors) {
    throw response.errors[0];
  }
  invalidateSleepScreensListCache();
  sleepScreenCache.delete(input.sleepScreenId);
  return response.data.updateSleepScreen;
}
